/**
 * Modul "Nutzerdaten": konsolidierte Verwaltung von Zugangsanfragen und
 * Benutzerkonten an einer Stelle - ausschließlich für den Systemadministrator
 * sichtbar und erreichbar (Route in App.tsx über ProtectedRoute
 * requireSuperAdmin abgesichert, exakt wie "Das Ende"; Menüpunkt in
 * Sidebar.tsx/MobileDrawer.tsx nur bei user.isSuperAdmin gerendert, kein
 * Teil des regulären Rollen-/Rechtesystems). Alle zugrunde liegenden
 * Endpunkte (/api/requests, /api/users) verlangen serverseitig weiterhin
 * "requests.manage" bzw. "users.manage" - der Systemadministrator
 * überspringt diese Prüfung wie überall im System automatisch.
 *
 * Bewusst KEIN Klartext-Passwort: beim Genehmigen einer Anfrage sowie beim
 * nachträglichen Setzen eines Passworts wird der eingegebene Wert sofort
 * sicher gehasht gespeichert (siehe backend/src/utils/crypto.ts) - dieselbe
 * Vorgehensweise wie im normalen Anmeldesystem, damit auch dieser
 * Admin-Bereich keine lesbaren Passwörter in der Datenbank hinterlässt.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Card, CardHeader } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { ApproveRequestModal } from "../requests/ApproveRequestModal";
import { formatDateTime } from "../../utils/formatDate";
import type { AccessRequest, Role, UserListItem } from "../../types";

const STATUS_LABELS: Record<AccessRequest["status"], string> = { open: "Offen", approved: "Genehmigt", rejected: "Abgelehnt" };
const STATUS_TONE: Record<AccessRequest["status"], "warning" | "success" | "danger"> = { open: "warning", approved: "success", rejected: "danger" };

type Tab = "requests" | "users";

export default function NutzerDaten() {
  const [tab, setTab] = useState<Tab>("requests");

  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  const [approving, setApproving] = useState<AccessRequest | null>(null);
  const [editingUser, setEditingUser] = useState<UserListItem | null | undefined>(undefined);
  const [passwordTarget, setPasswordTarget] = useState<UserListItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqResult, userList, roleList] = await Promise.all([
        api.get<{ items: AccessRequest[]; total: number }>("/api/requests?pageSize=100"),
        api.get<UserListItem[]>("/api/users"),
        api.get<Role[]>("/api/roles"),
      ]);
      setRequests(reqResult.items);
      setUsers(userList);
      setRoles(roleList);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleReject(r: AccessRequest) {
    if (!confirm(`Anfrage von "${r.full_name}" wirklich ablehnen?`)) return;
    try {
      await api.post(`/api/requests/${r.id}/reject`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Ablehnen fehlgeschlagen.");
    }
  }

  async function toggleActive(u: UserListItem) {
    try {
      await api.patch(`/api/users/${u.id}/status`, { isActive: !u.is_active });
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    }
  }

  async function handleDeleteUser(u: UserListItem) {
    if (!confirm(`Benutzer "${u.full_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const openRequests = requests.filter((r) => r.status === "open");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Nutzerdaten</h1>
        <p className="text-sm text-gray-500 mt-1">Zugangsanfragen und Benutzerkonten an einer Stelle verwalten.</p>
      </div>

      <div className="flex gap-2 border-b border-surface-border">
        <button
          onClick={() => setTab("requests")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "requests" ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
        >
          Zugangsanfragen{openRequests.length > 0 && <span className="ml-1.5 text-xs">({openRequests.length})</span>}
        </button>
        <button
          onClick={() => setTab("users")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "users" ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
        >
          Benutzer
        </button>
      </div>

      {loading ? (
        <Spinner />
      ) : tab === "requests" ? (
        <Card padded={false} className="overflow-x-auto">
          <div className="px-5 pt-4"><CardHeader title="Zugangsanfragen" subtitle="Alle über das System eingegangenen Anfragen." /></div>
          {requests.length === 0 ? (
            <EmptyState message="Keine Anfragen vorhanden." />
          ) : (
            <table className="table-base">
              <thead>
                <tr><th>Name</th><th>Gewünschte Rolle</th><th>Datum</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id}>
                    <td className="font-medium text-gray-100">{r.full_name}</td>
                    <td><Badge tone="accent">{r.requested_role_name}</Badge></td>
                    <td className="text-gray-500 text-xs whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                    <td>
                      <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABELS[r.status]}</Badge>
                      {r.status === "approved" && r.approved_by_name && <span className="block text-gray-600 text-[11px] mt-0.5">von {r.approved_by_name}</span>}
                      {r.status === "rejected" && r.rejected_by_name && <span className="block text-gray-600 text-[11px] mt-0.5">von {r.rejected_by_name}</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {r.status === "open" && (
                        <>
                          <button className="text-accent text-xs hover:underline mr-3" onClick={() => setApproving(r)}>Genehmigen</button>
                          <button className="text-danger text-xs hover:underline" onClick={() => handleReject(r)}>Ablehnen</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
        <Card padded={false} className="overflow-x-auto">
          <div className="px-5 pt-4"><CardHeader title="Benutzer" subtitle="Alle bestehenden Benutzerkonten." /></div>
          {users.length === 0 ? (
            <EmptyState message="Keine Benutzer vorhanden." />
          ) : (
            <table className="table-base">
              <thead>
                <tr><th>Name</th><th>Benutzername</th><th>Rolle</th><th>Status</th><th>Letzter Login</th><th /></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium text-gray-100">{u.full_name}</td>
                    <td className="text-gray-400">{u.username}</td>
                    <td><Badge tone="accent">{u.role_name}</Badge></td>
                    <td>{u.is_active ? <Badge tone="success">Aktiv</Badge> : <Badge tone="gray">Deaktiviert</Badge>}</td>
                    <td className="text-gray-500 text-xs whitespace-nowrap">{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</td>
                    <td className="text-right whitespace-nowrap space-x-2">
                      <button className="text-accent text-xs hover:underline" onClick={() => setEditingUser(u)}>Bearbeiten</button>
                      <button className="text-gray-400 text-xs hover:underline" onClick={() => setPasswordTarget(u)}>Passwort setzen</button>
                      <button className="text-warning text-xs hover:underline" onClick={() => toggleActive(u)}>{u.is_active ? "Deaktivieren" : "Aktivieren"}</button>
                      <button className="text-danger text-xs hover:underline" onClick={() => handleDeleteUser(u)}>Löschen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {approving && (
        <ApproveRequestModal request={approving} onClose={() => setApproving(null)} onApproved={() => { setApproving(null); load(); }} />
      )}
      {editingUser !== undefined && editingUser && (
        <NutzerDatenUserForm user={editingUser} roles={roles} onClose={() => setEditingUser(undefined)} onSaved={() => { setEditingUser(undefined); load(); }} />
      )}
      {passwordTarget && (
        <SetPasswordModal user={passwordTarget} onClose={() => setPasswordTarget(null)} />
      )}
    </div>
  );
}

/** Bearbeitet Name, Benutzername und Rolle - im Gegensatz zur regulären Benutzerverwaltung ist der Benutzername hier änderbar. */
function NutzerDatenUserForm({ user, roles, onClose, onSaved }: { user: UserListItem; roles: Role[]; onClose: () => void; onSaved: () => void }) {
  const [username, setUsername] = useState(user.username);
  const [fullName, setFullName] = useState(user.full_name);
  const [roleId, setRoleId] = useState(user.role_id);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.put(`/api/users/${user.id}`, { username, fullName, roleId: Number(roleId) });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Benutzer bearbeiten: ${user.full_name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="nutzerdaten-user-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
        </>
      }
    >
      <form id="nutzerdaten-user-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Benutzername *</label>
          <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} minLength={3} required />
        </div>
        <div>
          <label className="label">Vollständiger Name *</label>
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} minLength={2} required />
        </div>
        <div>
          <label className="label">Rolle *</label>
          <select className="input" value={roleId} onChange={(e) => setRoleId(Number(e.target.value))} required>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Setzt ein neues Passwort für den Benutzer - wird sofort serverseitig
 * gehasht gespeichert (siehe PUT /api/users/:id/password), NICHT im
 * Klartext. Eigenes Modal statt window.prompt() für zuverlässiges Verhalten
 * (siehe Erfahrung mit "Neue Woche" in der Wochenabgabe).
 */
function SetPasswordModal({ user, onClose }: { user: UserListItem; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== passwordRepeat) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/api/users/${user.id}/password`, { newPassword: password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Modal title={`Passwort gesetzt: ${user.full_name}`} onClose={onClose} footer={<button className="btn-primary" onClick={onClose}>Schließen</button>}>
        <div className="text-center py-4 space-y-2">
          <p className="text-3xl">✅</p>
          <p className="text-gray-200 font-medium">Neues Passwort gespeichert.</p>
          <p className="text-sm text-gray-500">Bitte geben Sie es dem Benutzer sicher weiter - es wird nirgends im System angezeigt.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Passwort setzen: ${user.full_name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="set-password-form" disabled={submitting}>{submitting ? "Wird gespeichert…" : "Passwort setzen"}</button>
        </>
      }
    >
      <form id="set-password-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Neues Passwort *</label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoFocus />
        </div>
        <div>
          <label className="label">Passwort wiederholen *</label>
          <input className="input" type="password" value={passwordRepeat} onChange={(e) => setPasswordRepeat(e.target.value)} minLength={8} required />
        </div>
      </form>
    </Modal>
  );
}
